/**
 * NDI `url-address` direct-connect parsing + validation.
 *
 * An NDI service endpoint may carry an optional `?url=HOST:PORT` query
 * (`ndi://MACHINE/Channel?url=10.56.46.10:5961`). That address is handed to the
 * relay's GStreamer `ndisrc url-address=` property so it connects to the sender
 * **directly by IP:port, bypassing mDNS discovery** — required because the
 * Polygraf server (Triton) sits on a subnet that can't receive the senders'
 * link-local mDNS announcements, so the NDI finder can never resolve the name.
 * The server can still reach the sender's media port by unicast (routed), which
 * is exactly what a direct address enables.
 *
 * This module is the single source of truth for that address, reused by:
 *   - `services/mediamtx-paths.ts` — threads it into the `ndi-ingest.sh` command
 *   - `routes/proxy.ts` — SSRF gate on the dialed address
 *   - `trpc/routers/rooms.ts` — reject a malformed address at create/update
 *   - `components/service-modals.tsx` — inline form validation
 *
 * Pure + environment-agnostic (browser-safe).
 */
import { canonicalizeHost, isSsrfBlockedHost } from './network-allowlist.js';
/**
 * Pull the raw `?url=` value from an `ndi://` endpoint. Returns null when the
 * query is absent or the endpoint is unparseable (callers treat both as "no
 * direct address" — the source is then reached via mDNS as before).
 */
export function extractNdiUrlAddress(endpoint) {
    try {
        return new URL(endpoint).searchParams.get('url');
    }
    catch {
        return null;
    }
}
/** The only query key an NDI endpoint may carry. */
const NDI_ALLOWED_QUERY_KEYS = ['url'];
/**
 * Query keys on an NDI endpoint that aren't recognized; empty when clean.
 *
 * `searchParams.get('url')` is case-sensitive, so a typo (`?URL=`, `?uri=`,
 * `?adress=`) reads as **absent** rather than wrong — the endpoint is accepted
 * and silently falls back to mDNS discovery, which cannot work from the server's
 * subnet. The only symptom would be a 20s `runOnDemandStartTimeout` stall with
 * nothing pointing at the typo. Reject unknown keys instead.
 */
export function unknownNdiQueryKeys(endpoint) {
    try {
        // `forEach` (base DOM lib) rather than `keys()` (DOM.Iterable, which this
        // package's `lib: ["ES2022","DOM"]` config doesn't include — it only
        // resolved locally via a hoisted @types/node global augmentation, and the
        // clean per-package build in Docker doesn't have it).
        const unknown = [];
        new URL(endpoint).searchParams.forEach((_value, key) => {
            if (!NDI_ALLOWED_QUERY_KEYS.includes(key))
                unknown.push(key);
        });
        return unknown;
    }
    catch {
        return [];
    }
}
/**
 * A strict dotted-quad IPv4 literal: four decimal octets 0–255, no leading
 * zeros. Returns null for anything looser.
 *
 * Strictness is the point. `inet_aton`/`getaddrinfo` also accept decimal
 * (`2130706433`), octal (`0177.0.0.1`), hex (`0x7f000001`) and short (`10.0.0`)
 * forms — all of which resolve to addresses a prefix-based range check would
 * never see. Anything not in canonical form is rejected outright below rather
 * than range-checked.
 */
function parseDottedQuad(host) {
    const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
    if (!match)
        return null;
    const octets = match.slice(1, 5);
    if (octets.some((o) => o.length > 1 && o.startsWith('0')))
        return null;
    const nums = octets.map(Number);
    if (nums.some((n) => n > 255))
        return null;
    return [nums[0], nums[1], nums[2], nums[3]];
}
/**
 * Validate a bare `HOST:PORT` NDI sender address. Returns the parsed address
 * (host canonicalized), or null when malformed or SSRF-blocked.
 *
 * Policy:
 *   - shape `HOST:PORT` — a canonical dotted-quad IPv4 or a hostname; port 1–65535;
 *   - ALLOW RFC1918 (`10/8`, `172.16/12`, `192.168/16`) and public hosts — MUNI's
 *     AV capture hardware is addressed by bare RFC1918 IP. CGNAT (`100.64/10`) is
 *     also permitted; it is not a dial-out concern here;
 *   - BLOCK the shared SSRF blocklist (localhost / cloud metadata / `0.0.0.0`)
 *     plus IPv4 loopback (`127/8`), link-local (`169.254/16`, incl. IMDS) and
 *     `0/8`: the relay dials this address directly, so it must never reach itself
 *     or a metadata endpoint;
 *   - BLOCK `*.localhost` — RFC 6761 §6.3 reserves the whole TLD to loopback, so
 *     `foo.localhost` is as much a self-dial as `localhost`;
 *   - REJECT non-canonical numeric literals (decimal/octal/hex/short), which
 *     resolvers accept but the range checks above cannot see, and malformed dot
 *     runs (leading / consecutive / repeated-trailing), which no real address
 *     needs and which only create room for this check to disagree with the
 *     resolver.
 *
 * The tight charset (no spaces, quotes, or shell metacharacters) also keeps a
 * validated value safe to single-quote into the `ndi-ingest.sh` command. IPv6
 * literals are intentionally unsupported (they can't match the charset) — MUNI
 * AV LANs are IPv4, and IPv6 loopback/link-local would be blocked anyway.
 */
export function parseNdiUrlAddress(addr) {
    const match = /^([A-Za-z0-9.-]+):(\d{1,5})$/.exec(addr.trim());
    if (!match)
        return null;
    const host = canonicalizeHost(match[1]);
    const port = Number(match[2]);
    if (port < 1 || port > 65535)
        return null;
    // Malformed dot runs (leading, consecutive, or a second trailing dot that
    // survived canonicalization). No legitimate address needs them, and allowing
    // them only lets this validator's view drift from the resolver's.
    if (host.startsWith('.') || host.includes('..'))
        return null;
    if (isSsrfBlockedHost(host))
        return null;
    // RFC 6761 §6.3: the whole `localhost.` TLD resolves to loopback, so a
    // subdomain is a self-dial too. isSsrfBlockedHost only matches the bare name.
    if (host === 'localhost' || host.endsWith('.localhost'))
        return null;
    const quad = parseDottedQuad(host);
    if (quad) {
        if (quad[0] === 127)
            return null; // loopback /8
        if (quad[0] === 0)
            return null; // "this network" /8
        if (quad[0] === 169 && quad[1] === 254)
            return null; // link-local /16 (incl. IMDS)
        return { host, port };
    }
    // Not a canonical dotted-quad: reject anything a resolver could still read as
    // a numeric literal, so only real hostnames get through.
    if (/^[0-9.]+$/.test(host))
        return null; // decimal / octal / short forms
    if (/^0x/.test(host))
        return null; // hex form
    return { host, port };
}
//# sourceMappingURL=ndi-url-address.js.map